/*
 *
 * File: PassengerRegisterScreen.js
 *
 * Purpose: Passenger registration component file.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  14.04.2023
 * Modified: 14.04.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import axios from "axios";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import { getUserRegistrationData } from "../utils/utils";
import * as constants from "../utils/constants";
import { registrationDataValuesObj } from "../utils/dataObjects";
import { SEND_REGISTRATIONGDATA } from "../api/actionCodes";

//--------------------------------------------------------------
//--- CSS styles -----------------------------------------------
//--------------------------------------------------------------

//--- End Css styles -------------------------------------------

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const regEmail =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Server URL for React API
const URL_SERV = "https://api.drivepartner.com/multistop/";
const PHP_FILE_PASSENGERREGISTER = "passenger_registration.php";
const registrationDataFormValuesObj = registrationDataValuesObj;
//---End component variable declarations -------------------------

/**
 * Implementation of passenger registration screen component.
 *
 * @param {*} props
 * @returns JSX-HTML-DOM
 */
const PassengerRegisterScreen = (props) => {
  const { t, i18n } = useTranslation("common");
  const userData = props.userData;
  
  //--------------------------------------------------------------
  // REDUX -------------------------------------------------------
  //--------------------------------------------------------------
  const dispatch = useDispatch();

  //--------------------------------------------------------------
  // Component states
  //--------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState({
    email: null,
  });
  const [input, setInput] = useState({
    surname: "",
    lastName: "",
    street: "",
    zip: "",
    location: "",
    phone: "",
    username: "",
    password1: "",
    password2: "",
    email: "",
  });

  const [error, setError] = useState({
    surnameErrorText: "",
    surnameErrorColor: true,
    lastNameErrorText: "",
    lastNameErrorColor: true,
    phoneErrorText: "",
    phoneErrorColor: true,
    emailErrorText: "",
    emailErrorColor: true,
    usernameErrorText: "",
    usernameErrorColor: true,
    password1ErrorText: "",
    password1ErrorColor: true,
    password2ErrorText: "",
    password2ErrorColor: true,
    password1ConfirmationErrorText: "",
    password2ConfirmationErrorText: "",
  });
  const [infoBoxResult, setInfoBoxResult] = useState(constants.INFO_BOX_EMPTY);
  //--- End states -----------------------------------------------

  const userRegistrationDataObj = getUserRegistrationData(userData);

  //--------------------------------------------------------------
  //--- Will be called after "first" rendering! ------------------
  //--------------------------------------------------------------
  useEffect(() => {}, []);

  //--------------------------------------------------------------
  //--- Will be called only for "infoBoxResult" ------------------
  //--------------------------------------------------------------
  useEffect(() => {
    if (infoBoxResult !== constants.INFO_BOX_EMPTY) {
      window.scrollTo(0, 1500);
    } else {
      window.scrollTo(0, 0);
    }
  }, [infoBoxResult]);

  /**
   * Handle input text change event.
   *
   * @param {*} e form event
   */
  const handleChange = (e) => {
    //const name = e.target.name;
    //const value = e.target.value;

    if (e.key === "Enter") {
      setIsLoading(true);
    }
  };

  /**
   * Input change handler.
   *
   * @param {*} e event
   */
  const inputChangeHandler = (e) => {
    var inputValue = e.target.value;
    const nameField = e.target.name;

    setInput((prev) => ({
      ...prev,
      [nameField]: inputValue,
    }));

    validateInput(e);
  };

  /**
   * Validate all necessary input fields.
   *
   * @param {*} e input event
   */
  const validateInput = (e) => {
    let { name, value } = e.target;

    setError((prev) => {
      const stateObj = { ...prev, [name]: "" };

      switch (name) {
        case "surname":
          stateObj["surnameErrorText"] = "";
          stateObj["surnameErrorColor"] = false;

          if (!value) {
            stateObj["surnameErrorText"] = t("errortext.error-surname-empty");
            stateObj["surnameErrorColor"] = true;
          }
          break;
        case "lastName":
          stateObj["lastNameErrorText"] = "";
          stateObj["lastNameErrorColor"] = false;

          if (!value) {
            stateObj["lastNameErrorText"] = t("errortext.error-lastName-empty");
            stateObj["lastNameErrorColor"] = true;
          }
          break;
        case "phone":
          stateObj["phoneErrorText"] = "";
          stateObj["phoneErrorColor"] = false;

          if (!value) {
            stateObj["phoneErrorText"] = t("errortext.error-phone-empty");
            stateObj["phoneErrorColor"] = true;
          }
          break;
        case "email":
          stateObj["emailErrorText"] = "";
          stateObj["emailErrorColor"] = false;

          if (!value) {
            stateObj["emailErrorText"] = t("errortext.error-email-empty");
            stateObj["emailErrorColor"] = true;
          } else if (!regEmail.test(value)) {
            stateObj["emailErrorText"] = t("errortext.error-email-validation");
            stateObj["emailErrorColor"] = true;
          }
          break;
        case "username":
          stateObj["usernameErrorText"] = "";
          stateObj["usernameErrorColor"] = false;

          if (!value) {
            stateObj["usernameErrorText"] = t("errortext.error-username-empty");
            stateObj["usernameErrorColor"] = true;
          }
          break;
        case "password1":
          stateObj["password1ErrorText"] = "";
          stateObj["password1ErrorColor"] = false;
          stateObj["password1ConfirmationErrorText"] = "";
          stateObj["password2ConfirmationErrorText"] = "";

          if (!value) {
            stateObj["password1ErrorText"] = t(
              "errortext.error-password-empty"
            );
            stateObj["password1ErrorColor"] = true;
          } else if (value.length <= constants.MIN_PASSWORD_LENGTH) {
            stateObj["password1ErrorText"] = t(
              "errortext.error-password-validation"
            );
            stateObj["password1ErrorColor"] = true;
          } else if (value !== input.password2) {
            stateObj["password1ConfirmationErrorText"] = t(
              "errortext.error-password-confirm-validation"
            );
            stateObj["password1ErrorColor"] = true;
            stateObj["password2ErrorColor"] = true;
          } else if (value === input.password2) {
            stateObj["password1ErrorColor"] = true;
            stateObj["password2ErrorColor"] = true;
          }
          break;
        case "password2":
          stateObj["password2ErrorText"] = "";
          stateObj["password2ErrorColor"] = false;
          stateObj["password1ConfirmationErrorText"] = "";
          stateObj["password2ConfirmationErrorText"] = "";

          if (!value) {
            stateObj["password2ErrorText"] = t(
              "errortext.error-password-empty"
            );
            stateObj["password2ErrorColor"] = true;
          } else if (value.length <= constants.MIN_PASSWORD_LENGTH) {
            stateObj["password2ErrorText"] = t(
              "errortext.error-password-validation"
            );
            stateObj["password2ErrorColor"] = true;
          } else if (value !== input.password1) {
            stateObj["password2ConfirmationErrorText"] = t(
              "errortext.error-password-confirm-validation"
            );
            stateObj["password1ErrorColor"] = true;
            stateObj["password2ErrorColor"] = true;
          } else if (value === input.password1) {
            stateObj["password1ErrorColor"] = false;
            stateObj["password2ErrorColor"] = false;
          }
          break;
        default:
          break;
      }

      return stateObj;
    });
  };

  /**
   * Set background color for "surname" validation.
   *
   * @returns color code
   */
  const setBackgroundColorSurname = () => {
    if (error.surnameErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "lastName" validation.
   *
   * @returns color code
   */
  const setBackgroundColorLastName = () => {
    if (error.lastNameErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "phone" validation.
   *
   * @returns color code
   */
  const setBackgroundColorPhone = () => {
    if (error.phoneErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "email" validation.
   *
   * @returns color code
   */
  const setBackgroundColorEmail = () => {
    if (error.emailErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "username" validation.
   *
   * @returns color code
   */
  const setBackgroundColorUsername = () => {
    if (error.usernameErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "password 1" validation.
   *
   * @returns color code
   */
  const setBackgroundColorPassword1 = () => {
    if (error.password1ErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "password 2" validation.
   *
   * @returns color code
   */
  const setBackgroundColorPassword2 = () => {
    if (error.password2ErrorColor) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Validate input fields before send.
   *
   */
  const validateBeforeSend = () => {
    let success = true;

    if (!input.surname) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["surnameErrorText"]: t("errortext.error-surname-empty"),
      }));
    }

    if (!input.lastName) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["lastNameErrorText"]: t("errortext.error-lastName-empty"),
      }));
    }

    if (!input.phone) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["phoneErrorText"]: t("errortext.error-phone-empty"),
      }));
    }

    if (!input.username) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["usernameErrorText"]: t("errortext.error-username-empty"),
      }));
    }

    if (!input.email) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["emailErrorText"]: t("errortext.error-email-empty"),
      }));
    }

    if (!input.password1) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["password1ErrorText"]: t("errortext.error-password-empty"),
      }));
    }

    if (!input.password2) {
      success = false;
      setError((prev) => ({
        ...prev,
        ["password2ErrorText"]: t("errortext.error-password-empty"),
      }));
    }

    return success;
  };

  /**
   * Get all passenger registration values
   * and store into special registration data values object.
   */
  const getRegistrationDataValues = () => {
    registrationDataFormValuesObj.actionCode = SEND_REGISTRATIONGDATA;
    registrationDataFormValuesObj.driverID = userRegistrationDataObj.userID;
    registrationDataFormValuesObj.surname = input.surname;
    registrationDataFormValuesObj.lastName = input.lastName;
    registrationDataFormValuesObj.street = input.street;
    registrationDataFormValuesObj.zip = input.zip;
    registrationDataFormValuesObj.location = input.location;
    registrationDataFormValuesObj.phone = input.phone;
    registrationDataFormValuesObj.email = input.email;
    registrationDataFormValuesObj.username = input.username;
    registrationDataFormValuesObj.password = input.password1;
  };

  /**
   * Submit the login form with fields input check.
   *
   * @param {*} e form event
   */
  const onSubmit = (e) => {
    e.preventDefault();

    if (!validateBeforeSend()) return;

    confirmDialog({
      message: t("infotext.info-text-sendregistration"),
      header: t("infotext.info-text-sendregistration-dialogheader"),
      icon: "pi pi-question-circle",
      accept: () => sendDataToServer(),
      reject: () => null,
    });
  };

  /**
   * Send passenger registration data now to
   * DrivePartner-Server and receive response from server.
   */
  const sendDataToServer = () => {
    getRegistrationDataValues();
    setInfoBoxResult(constants.INFO_BOX_WAIT_CIRCLE);

    axios({
      method: "post",
      url: URL_SERV + PHP_FILE_PASSENGERREGISTER,
      data: registrationDataFormValuesObj,
    }).then((response) => {      
      if (response.data.Success) {
        setInfoBoxResult(constants.INFO_BOX_RESULT_SUCCESS);
      } else {
        if (response.data.PassengerData === "User exist") {
          setInfoBoxResult(constants.INFO_BOX_RESULT_USER_EXIST_ERROR);
        } else if (response.data.PassengerData === "Email exist") {
          setInfoBoxResult(constants.INFO_BOX_RESULT_EMAIL_EXIST_ERROR);
        } else {
          setInfoBoxResult(constants.INFO_BOX_RESULT_ERROR);
        }        
      }

      setTimeout(function () {
        setInfoBoxResult(constants.INFO_BOX_EMPTY);
      }, 6000);
    });
  };

  /**
   * Show result info after sending
   * registration data to server in an info box.
   */
  const showSendResultInfo = () => {
    switch (infoBoxResult) {
      case constants.INFO_BOX_WAIT_CIRCLE:
        return <ProgressSpinner style={{ width: "60px", height: "60px" }} />;
      case constants.INFO_BOX_RESULT_SUCCESS:
        return (
          <div className="success-box">
            <table>
              <tr>
                <td>
                  <i
                    className="pi pi-check-circle"
                    style={{ fontSize: "2em", paddingRight: "20px" }}
                  ></i>
                </td>
                <td style={{ verticalAlign: "center" }}>
                  {t("successtext.success-store-registration-text")}
                </td>
              </tr>
            </table>
          </div>
        );
      case constants.INFO_BOX_RESULT_USER_EXIST_ERROR:
        return (
          <div className="error-box">
            <table>
              <tr>
                <td>
                  <i
                    className="pi pi-exclamation-circle"
                    style={{ fontSize: "2em", paddingRight: "20px" }}
                  ></i>
                </td>
                <td style={{ verticalAlign: "center" }}>
                  {t("errortext.error-sendregistrationdata-user-exist-text")}
                </td>
              </tr>
            </table>
          </div>
        );
      case constants.INFO_BOX_RESULT_EMAIL_EXIST_ERROR:
        return (
          <div className="error-box">
            <table>
              <tr>
                <td>
                  <i
                    className="pi pi-exclamation-circle"
                    style={{ fontSize: "2em", paddingRight: "20px" }}
                  ></i>
                </td>
                <td style={{ verticalAlign: "center" }}>
                  {t("errortext.error-sendregistrationdata-email-exist-text")}
                </td>
              </tr>
            </table>
          </div>
        );
      case constants.INFO_BOX_RESULT_ERROR:
        return (
          <div className="error-box">
            <table>
              <tr>
                <td>
                  <i
                    className="pi pi-exclamation-circle"
                    style={{ fontSize: "2em", paddingRight: "20px" }}
                  ></i>
                </td>
                <td style={{ verticalAlign: "center" }}>
                  {t("errortext.error-sendregistrationdata-text")}
                </td>
              </tr>
            </table>
          </div>
        );
      default:
        return null;
    }
  };

  //--------------------------------------------------------------
  //--- Returns JSX code for "PassengerRegisterScreen ------------
  //--------------------------------------------------------------
  return (
    <>
      <Header userData={userData} style={{ left: "0", right: "0" }} />
      <ConfirmDialog />
      <div style={{ paddingLeft: "20px", paddingRight: "20px" }}>
        {/* Begin: form ************************************************/}
        <form onSubmit={onSubmit}>
          <div className="text-900 font-medium text-900 text-xl mt-6 mb-3">
            {t("screentext.screen-text-login-header1")} -{" "}
            {t("screentext.screen-text-registration")}
          </div>
          <div className="grid formgrid p-fluid">
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="surname"
                  name="surname"
                  maxlength="250"
                  className="w-full"
                  style={{ backgroundColor: setBackgroundColorSurname() }}
                  onChange={(e) => inputChangeHandler(e)}
                  onBlur={validateInput}
                />
                <label htmlFor="surname">
                  {t("formular.placeholder-surname")}
                </label>
              </span>
              {error.surnameErrorText && (
                <span className="error-text">{error.surnameErrorText}</span>
              )}
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="lastName"
                  name="lastName"
                  maxlength="250"
                  className="w-full"
                  style={{ backgroundColor: setBackgroundColorLastName() }}
                  onChange={(e) => inputChangeHandler(e)}
                  onBlur={validateInput}
                />
                <label htmlFor="lastName">
                  {t("formular.placeholder-lastname")}
                </label>
              </span>
              {error.lastNameErrorText && (
                <span className="error-text">{error.lastNameErrorText}</span>
              )}
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="username"
                  name="username"
                  maxlength="250"
                  className="w-full"
                  style={{ backgroundColor: setBackgroundColorUsername() }}
                  onChange={(e) => inputChangeHandler(e)}
                  onBlur={validateInput}
                />
                <label htmlFor="username">
                  {t("formular.placeholder-username")}
                </label>
              </span>
              {error.usernameErrorText && (
                <span className="error-text">{error.usernameErrorText}</span>
              )}
            </div>

            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="street"
                  name="street"
                  maxlength="250"
                  className="w-full"
                  onChange={(e) => inputChangeHandler(e)}
                />
                <label htmlFor="street">
                  {t("formular.placeholder-passenger-street")}
                </label>
              </span>
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="zip"
                  name="zip"
                  maxlength="250"
                  className="w-full"
                  onChange={(e) => inputChangeHandler(e)}
                />
                <label htmlFor="zip">
                  {t("formular.placeholder-passenger-zip")}
                </label>
              </span>
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="location"
                  name="location"
                  maxlength="250"
                  className="w-full"
                  onChange={(e) => inputChangeHandler(e)}
                />
                <label htmlFor="location">
                  {t("formular.placeholder-passenger-location")}
                </label>
              </span>
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="phone"
                  name="phone"
                  maxlength="250"
                  className="w-full"
                  style={{ backgroundColor: setBackgroundColorPhone() }}
                  onChange={(e) => inputChangeHandler(e)}
                  onBlur={validateInput}
                />
                <label htmlFor="phone">
                  {t("formular.placeholder-passenger-phone")}
                </label>
              </span>
              {error.phoneErrorText && (
                <span className="error-text">{error.phoneErrorText}</span>
              )}
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <InputText
                  id="email"
                  name="email"
                  maxlength="250"
                  className="w-full"
                  style={{ backgroundColor: setBackgroundColorEmail() }}
                  onChange={inputChangeHandler}
                  onBlur={validateInput}
                />
                <label htmlFor="email">
                  {t("formular.placeholder-changebooking-email")}
                </label>
              </span>
              {error.emailErrorText && (
                <span className="error-text">{error.emailErrorText}</span>
              )}
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <Password
                  id="password1"
                  name="password1"
                  maxlength="250"
                  inputStyle={{
                    width: "100%",
                    backgroundColor: setBackgroundColorPassword1(),
                  }}
                  style={{
                    width: "100%",
                    backgroundColor: setBackgroundColorPassword1(),
                  }}
                  autocomplete="new-password"
                  toggleMask
                  onChange={(e) => inputChangeHandler(e)}
                  onBlur={validateInput}
                />
                <label htmlFor="password1">
                  {t("formular.placeholder-password")}
                </label>
              </span>
              {error.password1ErrorText && (
                <span className="error-text">{error.password1ErrorText}</span>
              )}
              {error.password1ConfirmationErrorText && (
                <span className="error-text">
                  {error.password1ConfirmationErrorText}
                </span>
              )}
            </div>
            <div className="field mb-4 col-12 md:col-6">
              <span className="p-float-label">
                <Password
                  id="password2"
                  name="password2"
                  maxlength="250"
                  inputStyle={{
                    width: "100%",
                    backgroundColor: setBackgroundColorPassword2(),
                  }}
                  style={{
                    width: "100%",
                    backgroundColor: setBackgroundColorPassword2(),
                  }}
                  autocomplete="new-password"
                  toggleMask
                  onChange={(e) => inputChangeHandler(e)}
                  onBlur={validateInput}
                />
                <label htmlFor="passwd2">
                  {t("formular.placeholder-password-repeat")}
                </label>
              </span>
              {error.password2ErrorText && (
                <span className="error-text">{error.password2ErrorText}</span>
              )}
              {error.password2ConfirmationErrorText && (
                <span className="error-text">
                  {error.password2ConfirmationErrorText}
                </span>
              )}
            </div>
            <div className="field mb-4 col-12 md:col-12">
              {showSendResultInfo()}
            </div>
            <div className="surface-border border-top-1 opacity-50 mb-3 col-12"></div>
            <div className="col-12">
              <Button
                type="submit"
                label={t("formular.btn-send")}
                icon="pi pi-send"
                className="w-auto mt-3"
              />
              <div style={{ paddingBottom: "100px" }} />
            </div>
          </div>
        </form>
        {/* End: form **************************************************/}
      </div>
      <Footer userData={userData} />
    </>
  );
};

export default PassengerRegisterScreen;
